import { ImageList } from '@c/listing/components/ImageList';
import useMultiImageUpload from '@util/hooks/useMultiImageUpload';
import { cx } from 'class-variance-authority';
import { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useCreateListing } from '../_context';
import AddPhotoIcon from '../_assets/AddPhotoIcon';
import Spinner from '@ui/Spinner';

export const PhotoSelect = () => {
  const { form, product } = useCreateListing();
  const errors = form.formState.errors;

  const { uploadingImage, handleFileInputChange, uploadImages, deleteImage } =
    useMultiImageUpload({
      id: product.id,
      onUploadCallback: ({ paths, errors }) => {
        const remainingSlots = 10 - product.images.length;
        const newPaths = paths.slice(0, remainingSlots);
        form.setValue('images', [...product.images, ...newPaths]);

        if (errors.length) {
          form.setError('images', {
            type: 'manual',
            message: errors.join(', '),
          });
        }
      },
      onDeleteCallback: (paths) => form.setValue('images', paths),
      type: 'listing',
    });

  const isUploadDisabled = useMemo(
    () => product.images.length >= 10,
    [product.images.length]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const isImageFile = acceptedFiles[0].type.includes('image');

      if (!isImageFile) {
        form.setError('images', {
          type: 'manual',
          message: 'Only images are allowed',
        });
        return;
      }

      const remainingSlots = 10 - product.images.length;
      const filesToUpload = acceptedFiles.slice(0, remainingSlots);

      uploadImages(filesToUpload).then(({ paths, errors }) => {
        if (paths.length) {
          form.setValue('images', [...product.images, ...paths]);
        }

        if (errors.length) {
          form.setError('images', {
            type: 'manual',
            message: errors.join(', '),
          });
        }
      });
    },
    [form, product.images, uploadImages]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isUploadDisabled,
  });

  return (
    <div className="flex w-[30rem] flex-col gap-y-6 sm:w-[80rem]">
      <span className="text-[1.6rem] text-zinc-700">
        {product.images.length} of 10 photos
      </span>

      <div
        {...getRootProps()}
        className={cx(
          'flex h-[20rem] w-full items-center justify-center rounded-[1rem] border-2 border-dashed border-zinc-300 py-16',
          isUploadDisabled ? 'cursor-default opacity-50' : 'cursor-pointer'
        )}
      >
        <div className="flex w-[20rem] flex-col items-center gap-y-4 sm:w-[40rem]">
          {uploadingImage ? (
            <div className="flex h-full w-full items-center justify-center">
              <div className="flex items-center justify-center">
                <Spinner className="!h-32 !w-32" />
              </div>
            </div>
          ) : (
            <>
              <AddPhotoIcon className="h-20 w-20 text-zinc-500" />

              <div className="flex flex-col gap-y-2">
                <span className="text-center text-[1.8rem] font-semibold text-zinc-600">
                  {isUploadDisabled
                    ? 'Maximum photos reached'
                    : isDragActive
                    ? 'Drop the photos here'
                    : 'Add photos'}
                </span>

                {!isDragActive && !isUploadDisabled && (
                  <span className="text-center text-[1.8rem] text-zinc-600">
                    or drag and drop
                  </span>
                )}
              </div>

              <input
                {...getInputProps()}
                onChange={handleFileInputChange}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                disabled={isUploadDisabled}
              />
            </>
          )}
        </div>
      </div>

      {errors?.images && errors?.images?.message && (
        <p className="text-red-500">{errors?.images?.message}</p>
      )}

      <ImageList
        images={product.images}
        deleteImage={deleteImage}
        onMainImageSelect={(paths) => form.setValue('images', paths)}
      />
    </div>
  );
};
